/**
 * Loss Factor Service
 *
 * Frontend API client for looking up energy loss factors by code:
 *  - Marginal (transmission) Loss Factors  — by TNI code / DUID
 *  - Distribution Loss Factors             — by DLF code / NMI
 *
 * Backend: server/features/scopingAi/routes/lossFactorRoutes.ts
 *
 * @module lossFactorService
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export type MlfRow = {
  region: string;
  factor_type: 'load' | 'gen';
  direction: 'import' | 'export' | null;
  location: string | null;
  voltage_kv: string | null;
  tni_code: string | null;
  duid: string | null;
  connection_point_id: string | null;
  current_fy: string;
  current_mlf: number;
  prior_fy: string | null;
  prior_mlf: number | null;
};

export type DlfRow = {
  state: string;
  dnsp: string;
  table_name: string | null;
  network_level: string | null;
  nmi: string | null;
  dlf_code: string;
  current_fy: string;
  current_dlf: number;
  prior_fy: string | null;
  prior_dlf: number | null;
};

type ApiResponse<T> = { success: boolean; data?: T; error?: string };

const HEADERS = {
  'Content-Type': 'application/json',
  'x-api-key': 'scopingai',
};

const lookup = async <T>(path: string, code: string, fy?: string): Promise<ApiResponse<T[]>> => {
  try {
    const trimmed = code.trim();
    if (!trimmed) return { success: false, error: 'Code is required' };

    const params = new URLSearchParams({ code: trimmed });
    if (fy) params.set('fy', fy);

    const response = await fetch(`${API_BASE_URL}/api/loss-factors/${path}?${params.toString()}`, {
      method: 'GET',
      headers: HEADERS,
    });

    const result = (await response.json()) as ApiResponse<T[]>;
    if (!response.ok || !result.success) {
      return { success: false, error: result.error || `Lookup failed (${response.status})` };
    }
    return { success: true, data: result.data || [] };
  } catch (error: unknown) {
    return { success: false, error: error instanceof Error ? error.message : 'Lookup failed' };
  }
};

export const lossFactorService = {
  /** Look up Marginal Loss Factor rows by TNI code or DUID. */
  getMlf: (code: string, fy?: string): Promise<ApiResponse<MlfRow[]>> =>
    lookup<MlfRow>('mlf', code, fy),

  /** Look up Distribution Loss Factor rows by DLF code or NMI. */
  getDlf: (code: string, fy?: string): Promise<ApiResponse<DlfRow[]>> =>
    lookup<DlfRow>('dlf', code, fy),
};
